import React from 'react';
import { Box, Container, Heading, Text, VStack, SimpleGrid, Button } from '@chakra-ui/react';

const FREQUENCY_ORDER = { weekly: 0, biweekly: 1, monthly: 2 };

function frequencyKey(opt) {
    return String(opt || '').toLowerCase().replace(/[\s_-]/g, '');
}

function frequencyLabel(opt) {
    const key = frequencyKey(opt);
    if (key === 'weekly') return 'Every week';
    if (key === 'biweekly') return 'Every 2 weeks';
    if (key === 'monthly') return 'Every month';
    return String(opt);
}

function sortFrequencies(options) {
    return [...(options || [])].sort(
        (a, b) => (FREQUENCY_ORDER[frequencyKey(a)] ?? 9) - (FREQUENCY_ORDER[frequencyKey(b)] ?? 9)
    );
}

const BLURBS = {
    weekly: 'Same pickup day each week.',
    biweekly: 'A pickup every other week.',
    monthly: 'One pickup a month.',
};

/**
 * Recurring pickup section for the public tenant site.
 * Renders only when the shop has repeat intervals configured.
 */
export default function SiteRecurring({ config }) {
    const sc = config?.siteContent || {};
    const intervals = sortFrequencies(config?.frequencyInterval);
    if (sc.hideRecurring || intervals.length === 0) return null;

    const laundryId = config?.laundryId || '1';
    const themeColor = sc.themeColor || 'blue';
    const discount = Number(config?.recurringDiscount) || 0;
    const discountLabel = Number.isInteger(discount) ? String(discount) : String(discount);

    const startRecurring = (e) => {
        e.preventDefault();
        try {
            localStorage.setItem('selectedOrderType', 'frequency');
        } catch (_) { /* private mode */ }
        window.location.assign(`/${laundryId}`);
    };

    return (
        <Box id="recurring" py={{ base: 16, md: 20 }} bg="white">
            <Container maxW="1200px">
                <VStack spacing={4} textAlign="center" mb={10}>
                    <Text fontSize="sm" fontWeight="600" color={`${themeColor}.500`} textTransform="uppercase" letterSpacing="wide">
                        Recurring
                    </Text>
                    <Heading fontSize={{ base: '2xl', md: '4xl' }} color="gray.800">
                        Make it a regular pickup
                    </Heading>
                    <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.500" maxW="640px">
                        Set laundry on a schedule. We create each pickup and text you before we come.
                        You can still order just once.
                    </Text>
                    {discount > 0 && (
                        <Text fontSize="md" fontWeight="700" color={`${themeColor}.700`}>
                            Save {discountLabel}% on every recurring pickup
                        </Text>
                    )}
                </VStack>

                <SimpleGrid columns={{ base: 1, md: Math.min(intervals.length, 3) }} spacing={6} maxW="900px" mx="auto" mb={10}>
                    {intervals.map((opt) => (
                        <Box key={opt} bg="gray.50" borderRadius="2xl" p={{ base: 5, md: 6 }} textAlign="center">
                            <Text fontSize="lg" fontWeight="700" color="gray.800">{frequencyLabel(opt)}</Text>
                            <Text fontSize="sm" color="gray.500" mt={2}>
                                {BLURBS[frequencyKey(opt)] || 'A pickup on your schedule.'}
                            </Text>
                        </Box>
                    ))}
                </SimpleGrid>

                <VStack>
                    <Button as="a" href={`/${laundryId}`} onClick={startRecurring}
                        colorScheme={themeColor} size="lg" borderRadius="full" px={8}>
                        Start a recurring pickup
                    </Button>
                </VStack>
            </Container>
        </Box>
    );
}
